import React, { useEffect, useMemo, useRef, useState } from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import cardValidator from 'card-validator';
import { useForm } from 'react-hook-form';

import { analyticsService } from '../../../core/services';
import { creditCardUtils, locationUtils } from '../../../utils';
import { Track } from '../../../weborder/analytics';
import * as Api from '../../../weborder/Api';
import { ToCurrency } from '../../../weborder/BasketControl';
import { paymentApiServices } from '../../../weborder/services';
import { Utilities } from '../../../weborder/Utilities';
import { Button, Snackbar, TextField, Typography } from '../../atoms';
import { Tick as TickIcon } from '../../icons';
import { Dialog, LoadingSpinner } from '../../layouts';
import { CreditCardFormProps, FormInputs } from './types.d';
import {
  formatCreditCardField,
  formatCvvField,
  formatExpiryDateField,
  formatPostcode,
  validateCreditCard,
  validateCVV,
  validateExpiryDate,
  validatePostcode,
} from './utils/addCreditCardUtils';

type FormStatus = 'default' | 'disabled' | 'loading';

const isHttps = creditCardUtils.getIsHTTPS();

const useStyles = makeStyles((theme: Theme) => ({
  creditCardDialog: {
    '& .MuiDialogContent-root': {
      minHeight: '320px',
    },
    '& .MuiFormControl-root': {
      marginBottom: theme.spacing(1),
    },
  },
  title: {
    paddingBottom: theme.spacing(2.5),
  },
  valid: {
    color: theme.palette.success.main,
  },
  wideSpacing: {
    wordSpacing: theme.spacing(1),
  },
  saveCardConsentHelpText: {
    color: theme.palette.grey[800],
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(0.5),
  },
  inputLeft: {},
  inputRight: {
    padding: 0,
  },
  secondRow: {
    '& .MuiFormControl-root': {
      boxSizing: 'border-box',
      width: '50%',

      '&:first-child': {
        paddingRight: theme.spacing(1),
      },
      '&:last-child': {
        paddingLeft: theme.spacing(1),
      },
    },
  },
  thirdRow: {
    '& .MuiFormControl-root': {
      boxSizing: 'border-box',
      width: '50%',
      paddingRight: theme.spacing(1),
    },
  },
}));

export function AddCreditCardDialog(props: CreditCardFormProps): JSX.Element {
  const classes = useStyles();
  const {
    addCardPayment,
    cancelAddCardBridge,
    clearBasketStatus,
    configData,
    currency = Api.Currency.EUR,
    isFromCheckoutPage,
    currentLanguage,
    orderTotalToPay = 0,
    savedCardCount,
    setShowDialog,
    showDialog,
    translate,
  } = props;

  const {
    control,
    errors,
    formState,
    handleSubmit,
    register,
    setValue,
    triggerValidation,
    watch,
  } = useForm<FormInputs>({
    defaultValues: {
      ccNumber: '',
      cvvNumber: '',
      expDate: '',
      postcode: '',
    },
    validateCriteriaMode: 'all',
    submitFocusError: true,
  });
  const { isSubmitting } = formState;
  const { ccNumber, expDate, cvvNumber, postcode } = watch();

  const newCard = useRef<cardValidator.Card>();
  const validNumber = useRef<cardValidator.validNumber>();

  const [submitError, setSubmitError] = useState<Error>();
  const [formStatus, setFormStatus] = useState<FormStatus>('default');

  const isCancelFlag = useRef(true);
  const fieldStatus = isHttps ? 'default' : 'disabled';
  const isRequiredFieldsEmpty =
    ccNumber === '' || expDate === '' || cvvNumber === '';
  const isSubmitDisabled =
    isRequiredFieldsEmpty || !isHttps || !!Object.keys(errors).length;

  const isUKWhiteLabel = locationUtils.checkCountryCode(configData, 'GB');
  const isUSWhiteLabel = locationUtils.checkCountryCode(configData, 'US');
  const isCanadaWhiteLabel = locationUtils.checkCountryCode(configData, 'CA');
  const isPuertoRicoWhiteLabel = locationUtils.checkCountryCode(
    configData,
    'PR',
  );
  const showPostcodeField =
    isUKWhiteLabel ||
    isUSWhiteLabel ||
    isCanadaWhiteLabel ||
    isPuertoRicoWhiteLabel;

  const displayConsentToSaveCard = creditCardUtils.displayConsentToSaveCard();
  const showSaveCardConsent = displayConsentToSaveCard && isFromCheckoutPage;

  const showLoaderOverlay = isSubmitting && showSaveCardConsent;
  const showLoaderInline = isSubmitting && !showSaveCardConsent;
  const showNonFixedCtas =
    !isFromCheckoutPage && !showPostcodeField && Utilities.isIOSPhone;

  useEffect(
    () => () => {
      if (isCancelFlag.current) {
        if (isFromCheckoutPage) {
          clearBasketStatus();
        }

        cancelAddCardBridge();
      }
    },
    [],
  );

  useEffect(() => {
    if (showLoaderInline) {
      return setFormStatus('loading');
    }

    if (isSubmitDisabled) {
      return setFormStatus('disabled');
    }

    return setFormStatus('default');
  }, [isSubmitting, isSubmitDisabled]);

  useEffect(() => {
    if (ccNumber) {
      const creditCardValue: string = ccNumber.replace(/\D/g, '');
      const creditCard = cardValidator.number(ccNumber);
      const maxCardLengths = creditCard.card ? creditCard.card.lengths : [19];
      const showValidation =
        !creditCard.isPotentiallyValid &&
        maxCardLengths.includes(creditCardValue.length);

      const creditCardFormatted = formatCreditCardField(
        creditCardValue,
        creditCard.card,
      );

      setValue('ccNumber', creditCardFormatted);
      validNumber.current = creditCard;
      newCard.current = creditCard.card;

      if (showValidation) {
        triggerValidation('ccNumber');
      }

      if (creditCard.isValid) {
        triggerValidation('ccNumber');
        control.fieldsRef.current.expDate.ref.focus();
      }
    }
  }, [ccNumber]);

  useEffect(() => {
    if (expDate) {
      const expiryDateValue: string = expDate.replace(/\D/g, '');
      const expiryDate = cardValidator.expirationDate(expDate);
      const showValidation =
        !expiryDate.isPotentiallyValid && expiryDateValue.length > 3;
      const isValid = expiryDate.isValid && expiryDateValue.length > 3;

      const expiryDateFormatted = formatExpiryDateField(expiryDateValue);
      setValue('expDate', expiryDateFormatted);

      if (showValidation) {
        triggerValidation('expDate');
      }

      if (isValid) {
        triggerValidation('expDate');
        control.fieldsRef.current.cvvNumber.ref.focus();
      }
    }
  }, [expDate]);

  useEffect(() => {
    if (cvvNumber) {
      const cvvFormatted = formatCvvField(cvvNumber, newCard.current);
      setValue('cvvNumber', cvvFormatted);

      const isValid =
        validateCVV(validNumber.current)(cvvFormatted) &&
        cvvFormatted.length > 2;
      if (isValid && showPostcodeField) {
        control.fieldsRef.current.postcode.ref.focus();
      }
    }
  }, [cvvNumber]);

  useEffect(() => {
    if (postcode) {
      const postcodeFormatted = formatPostcode(postcode);
      setValue('postcode', postcodeFormatted);
    }
  }, [postcode]);

  const localisedOrderTotal = useMemo(
    () => ToCurrency(orderTotalToPay, currency, currentLanguage),
    [orderTotalToPay, currency, currentLanguage],
  );

  const trackPaidWithCardAndSavedCard = (hasPostcode: boolean): void => {
    const eventData = {
      amp_label: 'Paid With Card And Saved Card',
      amp_numOfSavedCards: savedCardCount,
      amp_postcodePresent: hasPostcode,
      label: 'Add New Card Done',
      section: 'Payments',
    };
    Track('Payments', 'Add New Card Done', eventData);
  };

  const trackPaidWithCardAndNotSaved = (hasPostcode: boolean): void => {
    const eventData = {
      amp_label: 'Paid With Card And Not Saved',
      amp_numOfSavedCards: savedCardCount,
      amp_postcodePresent: hasPostcode,
    };
    analyticsService.track(eventData);
  };

  const onCreditCardFieldBlur = (): void => {
    const validateCreditCardField =
      ccNumber.length &&
      !validNumber?.current?.isValid &&
      newCard?.current?.lengths.includes(ccNumber.length);

    if (validateCreditCardField) {
      triggerValidation('ccNumber');
    }
  };

  const onPostcodeFieldBlur = (): void => {
    const trimmedPostcode = postcode.trim();
    setValue('postcode', trimmedPostcode);
  };

  const onSubmit = (submitProps: { hasConsentToSaveCard: boolean }) =>
    handleSubmit(async (formInputs) => {
      try {
        // TODO LOCK ROUTER
        const { month, year } = cardValidator.expirationDate(
          formInputs.expDate,
        );

        const digitMonth = parseInt(month);
        const fourDigitYear =
          year.length === 4 ? parseInt(year) : parseInt(`20${year}`);
        const postalCode = formInputs?.postcode?.trim() || undefined;
        const hasPostcode = postalCode?.length > 0;

        const paymentAccountId = await paymentApiServices.createCard({
          cardNumber: formInputs.ccNumber,
          cvv: formInputs.cvvNumber,
          expMonth: digitMonth,
          expYear: fourDigitYear,
          hasConsentToSaveCard: submitProps.hasConsentToSaveCard,
          postalCode,
        });

        isCancelFlag.current = false;

        addCardPayment({
          cardNumber: formInputs.ccNumber,
          hasConsentToSaveCard: submitProps.hasConsentToSaveCard,
          paymentAccountId,
        });

        setShowDialog(false);

        submitProps.hasConsentToSaveCard
          ? trackPaidWithCardAndSavedCard(hasPostcode)
          : trackPaidWithCardAndNotSaved(hasPostcode);
      } catch (error) {
        setSubmitError(error);
        Track('Payments', 'Add New Card Failure');
      } finally {
        // TODO UNLOCK ROUTER
      }
    });

  const handleCloseDialog = (): void => {
    if (isCancelFlag.current) {
      if (isFromCheckoutPage) {
        clearBasketStatus();
      }

      cancelAddCardBridge();
    }
    setShowDialog(false);
  };

  const titleLabel = isFromCheckoutPage
    ? 'AddNewCard_field_addNewCardAndPay'
    : 'AddNewCard_field_add_a_new_card';

  const getDialogActions = (): JSX.Element => {
    if (showSaveCardConsent) {
      return (
        <>
          <Button
            data-fd="pay-and-save-card-button"
            fullWidth
            onClick={onSubmit({ hasConsentToSaveCard: true })}
            status={formStatus}
            type="submit"
          >
            {translate('AddNewCard_field_payNowAndSaveCard', {
              orderTotal: localisedOrderTotal,
            })}
          </Button>
          <Button
            data-fd="pay-and-dont-save-card-button"
            fullWidth
            onClick={onSubmit({
              hasConsentToSaveCard: false,
            })}
            status={formStatus}
            type="submit"
            variant="secondary"
          >
            {translate('AddNewCard_field_payAndDontSaveCard', {
              orderTotal: localisedOrderTotal,
            })}
          </Button>
        </>
      );
    }

    const saveCardButtonLabel = isFromCheckoutPage
      ? `${translate('AddNewCard_field_payNowAndSaveCard', {
          orderTotal: localisedOrderTotal,
        })}`
      : translate('AddNewCard_field_add_card');

    return (
      <Button
        data-fd="add-card-button"
        fullWidth
        onClick={onSubmit({ hasConsentToSaveCard: true })}
        status={formStatus}
        type="submit"
      >
        {saveCardButtonLabel}
      </Button>
    );
  };

  return (
    <Dialog
      actions={getDialogActions()}
      className={classes.creditCardDialog}
      dialogName="add-credit-card"
      onTitleClick={(): void => handleCloseDialog()}
      open={showDialog}
      useNonFixedCtas={showNonFixedCtas}
    >
      {showLoaderOverlay && <LoadingSpinner variant="panel" bgOverlay />}

      <header className="fd-header">
        <Typography
          className={classes.title}
          data-fd="dialog-add-new-card-title"
          variant="h1"
        >
          {translate(titleLabel)}
        </Typography>
      </header>

      {submitError && (
        <Snackbar message={submitError.message} severity="error" />
      )}

      {!isHttps && (
        <Snackbar
          message={`${translate('Cards_cannot_added_SSL')}`}
          severity="warning"
        />
      )}

      <form noValidate>
        <TextField
          autoComplete="cc-number"
          autoFocus
          data-fd="credit-card-number-field"
          data-recording-disable
          endIcon={
            validNumber.current?.isValid && (
              <span data-fd="credit-card-valid">
                <TickIcon className={classes.valid} />
              </span>
            )
          }
          error={!!errors.ccNumber}
          fullWidth
          helperText={
            errors?.ccNumber &&
            `${translate('AddNewCard_Warning_Invalid_card_number')}`
          }
          id="creditCard"
          inputProps={{
            className: classes.wideSpacing,
            inputMode: 'numeric',
            pattern: '[0-9]*',
            onBlur: onCreditCardFieldBlur,
          }}
          inputRef={register({
            validate: validateCreditCard,
          })}
          label={`${translate('AddNewCard_field_card_number')}`}
          name="ccNumber"
          placeholder="5678 9012 2345 4568"
          startIcon={creditCardUtils.getCardIcon(newCard.current)}
          status={fieldStatus}
        />

        <div className={classes.secondRow}>
          <TextField
            autoComplete="cc-exp"
            data-fd="credit-card-expiry-field"
            data-recording-disable
            error={!!errors.expDate}
            fullWidth
            helperText={
              errors?.expDate &&
              `${translate('AddNewCard_Warning_Invalid_date')}`
            }
            id="expDate"
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            inputRef={register({
              validate: validateExpiryDate,
            })}
            label={`${translate('AddNewCard_field_exp_date')}`}
            name="expDate"
            placeholder="MM/YY"
            status={fieldStatus}
          />

          <TextField
            autoComplete="cc-csc"
            data-fd="credit-card-cvv-field"
            data-recording-disable
            error={!!errors.cvvNumber}
            fullWidth
            helperText={
              errors?.cvvNumber &&
              `${translate('AddNewCard_Warning_InvalidCVC')}`
            }
            id="cvvNumber"
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            inputRef={register({
              validate: validateCVV(validNumber.current),
            })}
            label={`${translate('CVC_number')}`}
            name="cvvNumber"
            placeholder="CVC"
            status={fieldStatus}
          />
        </div>

        {showPostcodeField && (
          <div className={classes.thirdRow}>
            <TextField
              autoComplete="cc-postcode"
              data-fd="credit-card-postcode-field"
              data-recording-disable
              error={!!errors.postcode}
              helperText={
                errors?.postcode &&
                `${translate('AddNewCard_Warning_InvalidPostcode')}`
              }
              id="postcode"
              inputProps={{
                onBlur: onPostcodeFieldBlur,
              }}
              inputRef={register({
                validate: validatePostcode,
              })}
              label={`${translate('AddNewCard_field_billingPostcode')}`}
              name="postcode"
              placeholder={`${translate('Optional')}`}
              status={fieldStatus}
            />
          </div>
        )}

        <div className={classes.saveCardConsentHelpText}>
          {showSaveCardConsent ? (
            <Typography variant="caption">
              {translate('AddNewCard_field_savingYourCardMakesItAvailable')}{' '}
              <a
                href="https://www.flipdish.com/ie/consumer-information/card-details"
                rel="noopener noreferrer"
                target="_blank"
              >
                {translate('AddNewCard_field_onTheFlipdishPlatform')}
              </a>
              {'. '}
              {translate('AddNewCard_field_youCanDeleteYourCardAtAnyTime')}
            </Typography>
          ) : (
            <>
              <Typography variant="caption">
                {translate('AddNewCard_field_yourCardWillBeSavedForFuture')}
              </Typography>
              <Typography variant="caption">
                {translate('AddNewCard_field_youCanRemoveItAtAnyTime')}
              </Typography>
            </>
          )}
        </div>
      </form>
    </Dialog>
  );
}
